import { uploadApis } from "@payping/api";
import React, { Dispatch, SetStateAction, useEffect, useState } from "react";
import {
  Form,
  Input,
  Modal,
  Select,
  Space,
  TextArea,
  Tooltip,
  Upload,
  useMessagedAsyncFn,
} from "@payping/web-components";
import { Add, QuestionCircleOutlined } from "@payping/icons";
import { ValidUrl, ValidUrlWithLocalhost } from "@payping/utils/validation";
import { createClientApp, editClientApp, getClientAppList } from "@payping/redux/actions";
import { UploadChangeParam, UploadFile } from "@payping/web-components/components/Upload/types";

import { useAppDispatch } from "@/utils/redux";
import { useAppTranslation } from "@/utils/useAppTranslation";
import { ConfirmModalProps } from "@/components/DashIpg/ConfirmModal";
import { OauthType } from "./OauthTypes";
import { SwitchRow } from "@/core/component";
import { TOKEN_SCOPES } from "@/core/consts";

interface FormRef {
  formModalData: OauthType;
  open: boolean;
  setOpen: Dispatch<SetStateAction<boolean>>;
  setConfirmModalOpen: Dispatch<SetStateAction<boolean>>;
  setConfirmModalData: Dispatch<SetStateAction<ConfirmModalProps | undefined>>;
}

interface FormType {
  logoUri?: string | UploadChangeParam;
  isTest?: boolean;
  clientName?: string;
  clientUri?: string;
  expireMonths?: number;
  scopes?: string[];
  redirectUris: string[];
  description?: string;
}

export const OauthFormModal: React.FC<FormRef> = (props: FormRef) => {
  // =========================================
  // Init
  // =========================================
  const { formModalData, open, setOpen, setConfirmModalData, setConfirmModalOpen } = props;
  const { t } = useAppTranslation();
  const dispatch = useAppDispatch();
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [form] = Form.useForm<FormType>();
  const [hiddenScopes, setHiddenScopes] = useState<string[]>([...TOKEN_SCOPES.ADMIN_SCOPES]);

  useEffect(() => {
    setHiddenScopes((prev) =>
      (formModalData?.allowedScopes || []).reduce(
        (acc: string[], scope) => (prev.includes(scope) ? [...acc, scope] : acc),
        [],
      ),
    );
  }, [formModalData]);

  useEffect(() => {
    if (formModalData) {
      const { allowedScopes, ...data } = formModalData;
      form.setFieldsValue({
        ...data,
        scopes: allowedScopes?.length ? allowedScopes : ["pay:read", "pay:write"],
      });
      if (formModalData.logoUri) {
        setFileList([
          {
            name: "image",
            size: 0,
            type: "image/*",
            uid: "0",
            url: formModalData.logoUri,
          },
        ]);
      }
    }
  }, [form, formModalData]);

  function hideModal() {
    form.resetFields();
    setOpen(false);
  }

  const [{ loading: loadingSubmit }, onSubmit] = useMessagedAsyncFn(
    async (values: FormType) => {
      const { logoUri, scopes: formScopes, ...oauthForm } = values;

      if (formModalData.clientId) {
        await dispatch(
          editClientApp({
            ...oauthForm,
            clientId: formModalData.clientId,
            logo:
              typeof logoUri === "string"
                ? logoUri.split("/")[logoUri.split("/").length - 1]
                : logoUri?.fileList[0]?.response,
            scopes: formScopes?.filter((scope) => !hiddenScopes.includes(scope)),
          }),
        );
        hideModal();
      } else {
        const resultClient = await dispatch(
          createClientApp({
            ...oauthForm,
            logo:
              typeof logoUri === "string"
                ? logoUri.split("/")[logoUri.split("/").length - 1]
                : logoUri?.fileList[0]?.response,
            scopes: formScopes?.filter((scope) => !hiddenScopes.includes(scope)),
          }),
        );

        await dispatch(getClientAppList());

        setConfirmModalData({
          client: { backToList: true, clientId: resultClient.clientId, clientSecret: resultClient.clientSecret },
          showGuide: !resultClient.isTest,
        });
        setConfirmModalOpen(true);
      }
      hideModal();
    },
    [formModalData],
    undefined,
    { useProgress: true },
  );

  // =========================================
  // Render
  // =========================================
  return (
    <Modal
      title={formModalData.clientId ? t("oauth.editOauth") : t("oauth.addOauth")}
      maskClosable={false}
      open={open}
      okText={formModalData.clientId ? t("oauth.editOauth") : t("oauth.addOauth")}
      onOk={() => form.submit()}
      cancelText={t("return")}
      onCancel={() => hideModal()}
    >
      <Form<FormType> form={form} onFinish={onSubmit} layout={"vertical"}>
        <Form.Item<FormType> name="logoUri">
          <Upload
            listType={"picture-card"}
            requestFunction={uploadApis.miscWithFilePost}
            onChange={(info: UploadChangeParam): void => setFileList(info.fileList)}
            fileList={fileList}
            showUploadList={{ showPreviewIcon: true, showRemoveIcon: true }}
            allowedExtensions={["jpg", "jpeg", "png"]}
            disabled={loadingSubmit}
          >
            {fileList.length ? null : (
              <div>
                <Add width={"14px"} className={"tw-color-primary"} />
                <div className={"ant-upload-text"}>{t("uploadImage")}</div>
              </div>
            )}
          </Upload>
        </Form.Item>
        <Form.Item name="isTest" hidden={Boolean(formModalData.clientId)} valuePropName={"checked"}>
          <SwitchRow
            loading={loadingSubmit}
            label={
              <Tooltip
                placement="leftBottom"
                title={
                  <ul>
                    <li key={0}>{t("oauth.attention1")}</li>
                    <br />
                    <li key={1}>{t("oauth.attention2")}</li>
                    <br />
                    <li key={1}>{t("oauth.attention3")}</li>
                    <br />
                    <li key={1}>{t("oauth.attention4")}</li>
                  </ul>
                }
              >
                <Space>
                  {t("oauth.testOauth")}
                  <QuestionCircleOutlined width={"14px"} />
                </Space>
              </Tooltip>
            }
            checkedChildren={t("active")}
            unCheckedChildren={t("inactive")}
          />
        </Form.Item>
        <Form.Item label={t("oauth.clientName")} name="clientName" rules={[{ required: true }]}>
          <Input skeleton={loadingSubmit} />
        </Form.Item>
        <Form.Url<FormType>
          form={form}
          skeleton={loadingSubmit}
          name={"clientUri"}
          placeholder={"www.payping.ir"}
          label={t("url")}
          rules={[
            { required: true },
            ({ getFieldValue }) => ({
              validator(): Promise<void> {
                const url = getFieldValue("clientUri");
                const isTest = getFieldValue("isTest");
                return url && (isTest ? !ValidUrlWithLocalhost(url) : !ValidUrl(url))
                  ? Promise.reject(t("error.url"))
                  : Promise.resolve();
              },
            }),
          ]}
        />
        <Form.Item label={t("oauth.expireMonths")} name="expireMonths" rules={[{ required: true }]}>
          <Input onlyNumbers skeleton={loadingSubmit} />
        </Form.Item>
        <Form.Item label={t("oauth.scopes")} name="scopes" rules={[{ required: true }]}>
          <Select
            showArrow
            skeleton={loadingSubmit}
            mode="multiple"
            allowClear
            filterOption={(_, option) => (option?.value ? !hiddenScopes.includes(String(option?.value)) : false)}
          >
            <Select.OptGroup label={t("oauth.personalInfo")}>
              {TOKEN_SCOPES.IDENTITY_SCOPES.map((scope) => (
                <Select.Option key={scope} value={scope}>
                  {t(`scopes.${scope.replace(":", "")}`)}
                </Select.Option>
              ))}
            </Select.OptGroup>
            <Select.OptGroup label={t("oauth.applicationInfo")}>
              {TOKEN_SCOPES.USER_SCOPES.map((scope) => (
                <Select.Option key={scope} value={scope}>
                  {t(`scopes.${scope.replace(":", "")}`)}
                </Select.Option>
              ))}
            </Select.OptGroup>
            <Select.OptGroup label={t("oauth.specialScopes")}>
              {hiddenScopes.map((scope) => (
                <Select.Option key={scope} value={scope} disabled>
                  {t(`scopes.${scope.replace(":", "")}`)}
                </Select.Option>
              ))}
            </Select.OptGroup>
          </Select>
        </Form.Item>
        <Form.Item
          label={t("oauth.returnUrl")}
          name="redirectUris"
          rules={[
            { required: true },
            ({ getFieldValue }) => ({
              validator(_rule, value): Promise<void> {
                const getError = () => {
                  const isTest = getFieldValue("isTest");
                  for (let i = 0; i < value.length; i += 1) {
                    if (isTest ? !ValidUrlWithLocalhost(value[i]) : !ValidUrl(value[i])) return t("error.url");
                  }
                  return undefined;
                };
                const error = getError();
                return value && error ? Promise.reject(error) : Promise.resolve();
              },
            }),
          ]}
        >
          <Select placeholder="https://www.payping.ir" mode="tags" skeleton={loadingSubmit} />
        </Form.Item>
        <Form.Item label={t("description")} name="description" rules={[{ required: true }]}>
          <TextArea skeleton={loadingSubmit} />
        </Form.Item>
      </Form>
    </Modal>
  );
};
